import { IAftnMessage, ISnowtam } from '@naviair-utm/node-shared-interfaces';
import { Collapse } from 'antd';
import moment from 'moment';
import React from 'react';
import { convertTime, tag } from '../InfoPage/TableCol/helpers';
import { IAftnMessageFC } from './AftnMessages';
import { TSnowtamGroup } from '../../interfaces';
import './styles.scss';

const stripSnowtam = (snowtam: string) => {
	// Remove all text until the word "SNOWTAM"
	return snowtam.substring(snowtam.indexOf('SNOWTAM'));
};

const isSnowtamExpired = (message: IAftnMessage<ISnowtam>): boolean => {
	const end = moment.utc(message.validity?.end);
	const now = moment.utc();
	return end.isBefore(now) || message.parsedMessage.expired === true || message.parsedMessage.replaced === true;
};

/** Get validity dates for a SNOWTAM message */
const getValidityDates = (message: IAftnMessage<ISnowtam>) => ({
	start: moment.utc(message.validity?.start).toDate(),
	end: moment.utc(message.validity?.end).toDate(),
});

export const SnowtamHeader: React.FC<IAftnMessageFC<ISnowtam>> = (props) => {
	const { start, end } = getValidityDates(props.message);
	const isExpired = isSnowtamExpired(props.message);

	return (
		<div className={'aftnRow aftnHeader'} key={`snowtam_header_${props.index}`}>
			<div className={'aftnCol bold'}>{`${props.message.icao ?? ''}`}</div>
			<div className={'aftnCol col-3 col-mobile-0'}></div>
			<div className={'aftnCol'}>{tag(convertTime(start), isExpired)}</div>
			<div className={'aftnCol'}>{tag(convertTime(end), isExpired)}</div>
		</div>
	);
};

// Full snowtam item display (header + content)
const SnowtamItem: React.FC<{ message: IAftnMessage<ISnowtam>; showIcao?: boolean }> = React.memo(({ message, showIcao = false }) => {
	const { start, end } = getValidityDates(message);
	const isExpired = isSnowtamExpired(message);

	return (
		<div>
			<div className={'aftnRow aftnHeader'}>
				{showIcao && <div className={'aftnCol bold'}>{message.icao ?? ''}</div>}
				{showIcao && <div className={'aftnCol col-3 col-mobile-0'}></div>}
				<div className={'aftnCol'}>{tag(convertTime(start), isExpired)}</div>
				<div className={'aftnCol'}>{tag(convertTime(end), isExpired)}</div>
			</div>
			<div className={'aftnRow aftnContent noPadding'}>
				<div className={'aftnCol italic'}>{stripSnowtam(message.rawInput)}</div>
			</div>
		</div>
	);
});

// Grouped snowtam display with accordion for multiple snowtams at same location
export const SnowtamGrouped: React.FC<{ group: TSnowtamGroup }> = ({ group }) => {
	const { icao, snowtams } = group;

	if (snowtams.length === 0) {
		return null;
	}

	const [mostRecent, ...older] = snowtams;

	return (
		<div className={'snowtamGroupContainer'}>
			{/* Always show the most recent snowtam */}
			<SnowtamItem message={mostRecent} showIcao={true} />

			{/* Show accordion for older snowtams if any exist */}
			{older.length > 0 && (
				<Collapse
					ghost
					expandIconPosition={'end'}
					className={'snowtamAccordion'}
				>
					<Collapse.Panel
						header={<span className={'olderSnowtamsLabel'}>{`${older.length} older SNOWTAM${older.length > 1 ? 's' : ''}`}</span>}
						key={'older'}
					>
						{older.map((snowtam, index) => (
							<div key={`older_snowtam_${index}`} className={'olderSnowtamItem'}>
								<SnowtamItem message={snowtam} showIcao={false} />
							</div>
						))}
					</Collapse.Panel>
				</Collapse>
			)}
		</div>
	);
};
